import { cdnHash } from '@cigam/template/dist/utils';
import { useSnackbar } from 'notistack';

import { postRequestConfig } from 'src/services/requests';
import { ChatPromptType, CiaGroup } from '../constants/cia';
import { canShowGenerativeOptions, setElementValue } from '../utils/ciaElement';
import { readHtmlPage } from '../utils/ciaHtml';
import {
  buildAgentInvokerXml,
  parseAgentsXml,
  parseChatPromptResultXml,
  parseGenerativeOptionsXml,
} from '../utils/ciaXml';
import { getAccessToken } from './authService';
import { loadDireitos } from './direitosService';

import type {
  ICiaAgent,
  ICiaAgentsResult,
  ICiaAuthenticationContext,
  ICiaGenerativeOption,
} from '../types/cia';

type EnqueueSnackbar = ReturnType<typeof useSnackbar>['enqueueSnackbar'];

const applyAgentRights = async (agents: ICiaAgent[]): Promise<ICiaAgent[]> => {
  const agentRights = agents.flatMap(agent => agent.rights);
  const uniqueRights = Array.from(new Set(agentRights)).filter(Boolean);

  if (!uniqueRights.length) {
    return agents.map(agent => ({
      ...agent,
      hasRights: true,
    }));
  }

  const allowedRights = await loadDireitos(uniqueRights);
  const allowedRightsSet = new Set(allowedRights);

  return agents.map(agent => {
    const hasRights =
      !agent.rights.length ||
      agent.rights.every(right => allowedRightsSet.has(right));

    return {
      ...agent,
      hasRights,
    };
  });
};

const getCiaServicesAgents = async (
  ciaAuthenticationContext: ICiaAuthenticationContext
): Promise<ICiaAgentsResult> => {
  try {
    const { cnpjCpf, nomeUsuario, codigoUsuario, apiDatabase, ciaServiceUrl } =
      ciaAuthenticationContext;

    const authentication = `${cnpjCpf};${nomeUsuario};${codigoUsuario}`;

    const data = new URLSearchParams({
      message: CiaGroup.PORTAIS,
      data: readHtmlPage(),
      group: CiaGroup.AGENTES,
    }).toString();

    const responseData = await new Promise<any>((resolve, reject) => {
      postRequestConfig(
        ciaServiceUrl,
        data,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            Authorization: cdnHash(),
            Authentication: authentication,
            'Generative-Api-Token-1': 'sk-',
            'Api-Database': apiDatabase,
          },
          responseType: 'text',
        },
        resolve,
        reject
      );
    });

    const result = parseChatPromptResultXml(responseData);

    if (!result || !result.success) {
      console.error(
        result?.errorMessage ||
          result?.message ||
          'Os agentes não estão disponíveis nesse momento.'
      );

      return {
        agents: [],
        message:
          result?.message || 'Os agentes não estão disponíveis nesse momento.',
        errorMessage: result?.errorMessage,
      };
    }

    const agents = parseAgentsXml(result.message || '');
    const agentsWithRights = await applyAgentRights(agents);

    return {
      agents: agentsWithRights,
    };
  } catch (error) {
    console.error('Ocorreu um erro inesperado para buscar os agentes.', error);

    return {
      agents: [],
      message: 'Ocorreu um erro inesperado para buscar os agentes.',
      errorMessage: String(error),
    };
  }
};

const getCiaServicesMenuOptions = async (
  ciaAuthenticationContext: ICiaAuthenticationContext,
  enqueueSnackbar: EnqueueSnackbar
): Promise<ICiaGenerativeOption[]> => {
  try {
    const { ciaServiceUrl } = ciaAuthenticationContext;

    const responseData = await new Promise<any>((resolve, reject) => {
      postRequestConfig(
        ciaServiceUrl,
        null,
        {
          headers: {
            Authorization: cdnHash(),
            'Generative-Menu': '*',
          },
          responseType: 'text',
        },
        resolve,
        reject
      );
    });

    return parseGenerativeOptionsXml(responseData);
  } catch (error) {
    console.error(
      'Ocorreu um erro inesperado para buscar as opções generativas.',
      error
    );

    enqueueSnackbar(
      'Ocorreu um erro inesperado para buscar as opções generativas.',
      {
        variant: 'error',
      }
    );

    return [];
  }
};

const invokeCiaAgent = async (
  agent: ICiaAgent,
  ciaAuthenticationContext: ICiaAuthenticationContext,
  onValueResult: (title: string, content: string) => void,
  enqueueSnackbar: EnqueueSnackbar,
  setLoading: (loading: boolean) => void
): Promise<void> => {
  if (!agent || !agent.xml) {
    enqueueSnackbar('Não foi possível processar os dados do Agente.', {
      variant: 'error',
    });

    return;
  }

  const { apiDatabase, apiUrl, ciaServiceUrl } = ciaAuthenticationContext;

  setLoading(true);

  try {
    const responseData = await new Promise<any>((resolve, reject) => {
      postRequestConfig(
        `${apiUrl}/api/invoker/invoke`,
        buildAgentInvokerXml(agent.xml),
        {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/xml; charset=UTF-8',
            'Request-Content-Type': 'application/xml',
            'Response-Content-Type': 'application/xml',
            'Api-Database': apiDatabase,
            'Assembly-Server': ciaServiceUrl,
          },
          responseType: 'text',
        },
        resolve,
        reject
      );
    });

    const result = parseChatPromptResultXml(responseData);

    const success = result?.success;
    const message = result?.message;
    const errorMessage = result?.errorMessage;
    const resultant = result?.resultant;

    if (!success) {
      enqueueSnackbar(
        message || `Não foi possível processar o ${agent.name}.`,
        {
          variant: 'error',
        }
      );

      console.error(
        `Não foi possível processar o ${agent.name}. Erro: ${
          errorMessage || 'sem detalhes'
        }`
      );

      return;
    }

    if (resultant === ChatPromptType.VALUE) {
      onValueResult(agent.name, message || '');

      return;
    }

    if (
      resultant === ChatPromptType.WEB_DOCUMENT ||
      resultant === ChatPromptType.URL
    ) {
      let targetUrl = message || '';

      if (resultant === ChatPromptType.WEB_DOCUMENT) {
        const blob = new Blob([message || ''], { type: 'text/html' });
        targetUrl = URL.createObjectURL(blob);
      }

      const newPage = window.open(targetUrl, '_blank');

      if (!newPage || newPage.closed) {
        enqueueSnackbar(
          'O agente foi processado com sucesso, porém o navegador bloqueou a abertura da nova aba. Verifique as configurações de pop-up e tente novamente.',
          {
            variant: 'info',
          }
        );
      }

      return;
    }

    enqueueSnackbar(
      `Não foi possível processar o ${agent.name} devido a um tipo de retorno inválido.`,
      {
        variant: 'error',
      }
    );

    console.error(
      "A propriedade 'resultant' não retornou nenhum tipo compatível para o objeto 'ChatPromptType'."
    );
  } catch (error) {
    console.error(
      `Ocorreu um erro interno ao processar o ${agent.name}.`,
      error
    );

    enqueueSnackbar(`Ocorreu um erro interno ao processar o ${agent.name}.`, {
      variant: 'error',
    });
  } finally {
    setLoading(false);
  }
};

const executeCiaGenerativeAI = async (
  option: ICiaGenerativeOption,
  previousActiveElement: Element | null,
  ciaAuthenticationContext: ICiaAuthenticationContext,
  enqueueSnackbar: EnqueueSnackbar,
  setLoading: (loading: boolean) => void
): Promise<void> => {
  if (!canShowGenerativeOptions(previousActiveElement)) {
    enqueueSnackbar(
      'Para utilizar a Geração de conteúdo posicione-se em um campo de texto que não esteja vazio e contenha mais de três palavras.',
      {
        variant: 'warning',
      }
    );

    return;
  }

  const { cnpjCpf, nomeUsuario, codigoUsuario, apiDatabase, ciaServiceUrl } =
    ciaAuthenticationContext;

  const authentication = `${cnpjCpf};${nomeUsuario};${codigoUsuario}`;

  const currentValue = previousActiveElement.value || '';
  const prompt = option.value.replace('[value]', currentValue);

  setLoading(true);

  try {
    const data = new URLSearchParams({
      message: `${prompt}\nE não adicione nenhuma explanação na resposta.`,
      group: CiaGroup.IA_GENERATIVA,
    }).toString();

    const responseData = await new Promise<any>((resolve, reject) => {
      postRequestConfig(
        ciaServiceUrl,
        data,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            Authorization: cdnHash(),
            Authentication: authentication,
            'Generative-Api-Token-1': 'sk-',
            'Api-Database': apiDatabase,
          },
          responseType: 'text',
        },
        resolve,
        reject
      );
    });

    const result = parseChatPromptResultXml(responseData);

    if (result.success) {
      const filled = setElementValue(
        previousActiveElement,
        result.message || ''
      );

      if (!filled) {
        enqueueSnackbar('Não foi possível preencher o campo selecionado.', {
          variant: 'warning',
        });
      }

      return;
    }

    console.error(
      'Ocorreu um erro ao executar a função de geração de conteúdo.',
      result.errorMessage
    );

    enqueueSnackbar(
      result.message || 'Não foi possível executar a geração de conteúdo.',
      {
        variant: 'error',
      }
    );
  } catch (error) {
    console.error(
      'Ocorreu um erro interno ao executar a função de geração de conteúdo.',
      error
    );

    enqueueSnackbar(
      'Ocorreu um erro interno, e por isso não foi possível utilizar a opção da geração de conteúdo.',
      {
        variant: 'error',
      }
    );
  } finally {
    setLoading(false);
  }
};

export {
  getCiaServicesAgents,
  getCiaServicesMenuOptions,
  invokeCiaAgent,
  executeCiaGenerativeAI,
};
